import { useMemo } from 'react';

import { compareAsc, isValid, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { StatusBadge } from '@/components/common';
import type { ColDef } from '@/grid/types';
import { dateOnlyFormatter } from '@/grid/value-formatters/dateOnlyFormatter';
import type { OnFarmMatingRecord } from '@/services/on-farm-implant';

import { getMatingBalance } from './balance';

export type FreshMatingRow = OnFarmMatingRecord & {
  createdCount: number;
  queuedCount: number;
  discardedCount: number;
};

export const useFreshMatingColumnDefs = (): ColDef<FreshMatingRow>[] => {
  const { t } = useTranslation();

  return useMemo<ColDef<FreshMatingRow>[]>(() => {
    const balancedLabel = t('onFarmImplant.fresh.balance.balanced');
    const needsReviewLabel = t('onFarmImplant.fresh.balance.needsReview');
    const balanceColors = {
      [balancedLabel]: { bg: 'bg-green-100', text: 'text-green-700' },
      [needsReviewLabel]: { bg: 'bg-red-100', text: 'text-red-700' },
    };
    const workedBalance = (params: { data?: FreshMatingRow }) => {
      const balance = getMatingBalance(params.data?.allocation);
      return balance?.hasActivity ? balance : null;
    };

    return [
      {
        colId: 'select',
        headerName: '',
        width: 44,
        maxWidth: 44,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        headerCheckboxSelectionFilteredOnly: true,
        suppressMovable: true,
        lockPosition: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
        cellClassRules: {
          'bg-tog-light-red': (params) => {
            const difference = workedBalance(params)?.difference;
            return difference !== undefined && difference !== 0;
          },
        },
      },
      {
        colId: 'expand',
        headerName: '',
        cellRenderer: 'agGroupCellRenderer',
        width: 36,
        maxWidth: 36,
        suppressMovable: true,
        lockPosition: true,
        sortable: false,
        filter: false,
        suppressHeaderMenuButton: true,
      },
      {
        field: 'recordName',
        headerName: t('onFarmImplant.fresh.name'),
        flex: 1.2,
        minWidth: 140,
        cellClass: 'font-semibold',
        valueGetter: (params) => params.data?.recordName ?? params.data?.id,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'donorName',
        headerName: t('onFarmImplant.fresh.donor'),
        flex: 1,
        minWidth: 120,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'sireName',
        headerName: t('onFarmImplant.fresh.sire'),
        flex: 1,
        minWidth: 120,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'ranchId',
        headerName: t('onFarmImplant.fresh.ranchId'),
        width: 120,
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'date',
        headerName: t('onFarmImplant.fresh.fertDate'),
        width: 120,
        valueFormatter: dateOnlyFormatter,
        filter: 'agDateColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
          comparator: (
            filterLocalDateAtMidnight: Date,
            cellValue: string | null | undefined,
          ) => {
            const cellDay = cellValue ? parseISO(cellValue.slice(0, 10)) : null;
            return cellDay && isValid(cellDay)
              ? compareAsc(cellDay, filterLocalDateAtMidnight)
              : -1;
          },
        },
      },
      {
        colId: 'plate',
        headerName: t('onFarmImplant.fresh.allocation.plate'),
        width: 90,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
        valueGetter: (params) => params.data?.allocation?.plate,
      },
      {
        colId: 'priority',
        headerName: t('onFarmImplant.fresh.allocation.priority'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
        valueGetter: (params) => params.data?.allocation?.priority,
      },
      {
        field: 'createdCount',
        headerName: t('onFarmImplant.fresh.created'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'queuedCount',
        headerName: t('onFarmImplant.fresh.queued'),
        width: 100,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        field: 'discardedCount',
        headerName: t('onFarmImplant.fresh.discarded'),
        width: 110,
        type: 'numericColumn',
        filter: 'agNumberColumnFilter',
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
      {
        colId: 'balance',
        headerName: t('onFarmImplant.fresh.balance.title'),
        width: 130,
        valueGetter: (params) => {
          const balance = workedBalance(params);
          if (!balance) return null;
          return balance.difference === 0 ? balancedLabel : needsReviewLabel;
        },
        tooltipValueGetter: (params) => {
          const difference = workedBalance(params)?.difference;
          if (!difference) return undefined;
          return difference > 0
            ? t('onFarmImplant.fresh.balance.unaccounted', {
                count: difference,
              })
            : t('onFarmImplant.fresh.balance.overLabShip', {
                count: -difference,
              });
        },
        cellRenderer: (params: { value: string | null }) =>
          params.value && (
            <StatusBadge label={params.value} colorMap={balanceColors} />
          ),
        filterParams: {
          buttons: ['apply', 'clear'],
          closeOnApply: true,
          maxNumConditions: 1,
        },
      },
    ];
  }, [t]);
};
